import { useState, useCallback, useEffect } from "react";
import _ from "lodash";

export function camelToWords(camel: string): string {
    camel = camel.trim();
    const words: string[] = [];
    let start = 0;
    for (let end = 1; end < camel.length; end++) {
        if ('A' <= camel[end] && camel[end] <= 'Z') {
            words.push(camel.substring(start, end).toLowerCase());
            start = end;
        }
    }
    words.push(camel.substring(start, camel.length).toLowerCase());

    return words.join(' ');
}

export function camelToTitle(camel: string): string {
    return camelToWords(camel).replace(/[a-z]/i, (ltr) => ltr.toUpperCase())
}

export function slashTrim(s: string): string {
    let start = 0;
    let end = s.length;
    if (s[start] === '/') start++;
    if (s[end - 1] === '/') end--;
    if (end <= start) return '';
    return s.substring(start, end);
}

export function quoteTrim(s: string): string {
    let start = 0;
    let end = s.length;
    if ((s[start] === '"' && s[end - 1] === '"')
        || (s[start] === "'" && s[end - 1] === "'")) {
        start++;
        end--;
    }
    return s.substring(start, end);
}

export function removeSuffix(s: string): string {
    let end = s.length - 1;
    while (end >= 0 && s[end] !== '.' && s[end] !== '/')
        end--;
    return end < 0 || s[end] === '/' ? s : s.substring(0, end);
}

export function getSuffix(s: string): string {
    let end = s.length - 1;
    while (end >= 0 && s[end] !== '.' && s[end] !== '/')
        end--;
    return end < 0 || s[end] === '/' ? '' : s.substring(end + 1);
}

export function makeSiteRelative(url: string): string {
    return url.replace(/^http(s)?:\/\/[^/]+\//, '/');
}

function setStateByPathInner(state: any, path: string[], value: any): any {
    if (Array.isArray(state)) {
        const newValue = setStateByPathInner(state[path[0]], _.tail(path), value);
        const newState = [ ...state];
        newState[path[0]] = newValue;
        return newState;
    } else if (typeof state === 'object') {
        const newValue = setStateByPathInner(state[path[0]], _.tail(path), value);
        return { ...state, [path[0]]: newValue };
    } else return value;
};

export function setStateByPath<T extends object>(state: T, path: string[], value: any): T {
    _.set(state, path, value);
    return setStateByPathInner(state, path, value);
}

export type StateTreeSetter<T extends object> = (pathOrSetter: string[] | ((val: T) => T), value?: any) => void;

export function useComplexState<T extends object>(init: T): [ T, StateTreeSetter<T> ] {
    const [ state, setState ] = useState<T>(init);

    const stateSetter = useCallback((pathOrSetter: string[] | ((val: T) => T), value?: any) => {
        if (Array.isArray(pathOrSetter)) {
            setState((state) => setStateByPath(state, pathOrSetter, value))
        } else {
            setState((state) => pathOrSetter(state));
        }
    }, []);

    return [ state, stateSetter ];
}

export function useAsyncInitialize<T>(init: T, load: () => Promise<T>): T {
    const [ state, setState ] = useState<T>(init);

    useEffect(() => {
        load().then((value: T) => setState(value));
    }, []);

    return state;
}

export function useAsyncCache<T>(init: T, load: (uid: number) => Promise<T>): [ T, () => void ] {
    const [ state, setState ] = useState<T>(init);
    const [ invalidation, setInvalidation ] = useState(0);

    useEffect(() => {
        load(invalidation).then((value: T) => setState(value));
    }, [ invalidation ]);

    const setInvalid = useCallback(() => setInvalidation(invalidation => invalidation + 1), []);

    return [ state, setInvalid ];
}

export function useToggle(init: boolean): [ boolean, () => void ] {
    const [ toggle, setToggle] = useState(init);
    return [ toggle, () => setToggle(value => !value) ];
}

export interface WindowSize {
    width?: number,
    height?: number
}

export function useWindowSize() {
    const [windowSize, setWindowSize] = useState<WindowSize>({
      width: undefined,
      height: undefined,
    });
  
    useEffect(() => {
      // Handler to call on window resize
      function handleResize() {
        // Set window width/height to state
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
      
      // Add event listener
      window.addEventListener("resize", handleResize);
      
      // Call handler right away so state gets updated with initial window size
      handleResize();
      
      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleResize);
    }, []); // Empty array ensures that effect is only run on mount
  
    return windowSize;
  }

export function forEachDeep(value: any, iteratee: (value: any, path: string[]) => void, path: string[] = []) {
    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) forEachDeep(value[i], iteratee, [ ...path, i.toString() ]);
    } else if (typeof value === 'object') {
        for (let key in value) forEachDeep(value[key], iteratee, [ ...path, key ]);
    } else {
        iteratee(value, path);
    }
}

export function pathJoin(...args: string[]): string {
    const stripped = args.filter(a => !!a);
    if (stripped.length === 0) return '';
    const startSlash = stripped[0].startsWith('/');
    const endSlash = stripped[stripped.length - 1].endsWith('/');
    let joined = stripped.map(a => slashTrim(a)).filter(a => !!a).join('/');
    if (startSlash) joined = '/' + joined;
    if (endSlash && joined !== '/') joined += '/';
    return joined;
}

export function withoutCaching(url: string) {
    const uid = Math.floor(Math.random() * Math.floor(1000000000));
    return url.indexOf('?') >= 0 ? url + '&_uid=' + uid : url + '?uid=' + uid;
}

export class Path {
    elements: string[] = [];
    extension: string = '';

    constructor(path: string | null) {
        if (path === null) return;
        this.elements = path.split('/');
        const dotSplit = this.elements[this.elements.length - 1].split('.');
        if (dotSplit.length > 1) {
            this.extension = dotSplit.pop() || '';
            this.elements[this.elements.length - 1] = dotSplit.join('.');
        }
    }

    toString(): string {
        return this.elements.join('/') + (this.extension ? "." + this.extension.toLowerCase() : "");
    }
}

